/**
 * What the invitee sees for each answer from POST /api/v2/invitations/accept.
 *
 * `retry` codes mean the acceptance decision is recorded but activation is unfinished,
 * so the same token will succeed shortly. `keepToken` keeps the token parked for another
 * attempt; everything else is final for that link.
 */

export type AcceptAction = "sign_in_other" | "dashboard" | "retry" | null;

export interface AcceptOutcome {
  message: string;
  retry: boolean;
  keepToken: boolean;
  action: AcceptAction;
}

const final = (message: string, action: AcceptAction = null): AcceptOutcome => ({
  message, retry: false, keepToken: false, action,
});

const OUTCOMES: Record<string, AcceptOutcome> = {
  token_required: final("This invite link is incomplete. Open the link from your invitation again."),
  not_found: final("This invite link is not valid. Ask the organization owner to send a new one."),
  email_mismatch: {
    message: "This invitation was sent to a different email address. Sign in with the invited address.",
    retry: false,
    keepToken: true,
    action: "sign_in_other",
  },
  expired: final("This invitation has expired. Ask the organization owner to resend it."),
  revoked: final("This invitation was withdrawn by the organization owner."),
  removed: final("You were removed from this organization, so this invitation can no longer be used."),
  taken: final("This invitation has already been accepted by another account."),
  inviter_not_authorized: final(
    "The person who invited you can no longer add members. Ask an organization owner to invite you again.",
  ),
  already_member: final(
    "You are already a member of this organization. Open it from the organization menu in the top bar.",
    "dashboard",
  ),
  suspended_member: final("Your membership in this organization is suspended. Contact the organization owner."),
  invalid_access: final(
    "This invitation's access settings are no longer valid. Ask the organization owner to update and resend it.",
  ),
  forbidden: final("Return to your own session to accept an invitation."),
};

const PENDING: AcceptOutcome = {
  message: "We are still finishing your invitation. Try again in a moment.",
  retry: true,
  keepToken: true,
  action: "retry",
};

export function describeAcceptError(code: string | null | undefined): AcceptOutcome {
  if (code === "in_progress" || code === "conflict") return PENDING;
  return OUTCOMES[String(code)] ?? {
    message: "We could not accept this invitation. Please try again.",
    retry: false,
    keepToken: true,
    action: "retry",
  };
}

export const PENDING_OUTCOME = PENDING;
